import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export interface StatisticsTab {
  value: string;
  label: string;
  render: () => React.ReactNode;
}

interface StatisticsDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  /** The first tab opens by default. */
  tabs: StatisticsTab[];
}

/**
 * Shell shared by Leader → Statistics and Project Head → Statistics: the
 * Analytics Dashboard's frame and tab strip, with scoped tabs inside. Only the
 * active tab is mounted, so a tab loads when it is first opened.
 */
const StatisticsDialog: React.FC<StatisticsDialogProps> = ({ open, onClose, title, subtitle, tabs }) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.value ?? '');
  const active = tabs.find((t) => t.value === activeTab) ?? tabs[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '78vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Tabs
            value={active?.value ?? false}
            onChange={(_, value: string) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
              '& .MuiTab-root': { color: 'rgba(var(--color-text-rgb), 0.68)', minHeight: 44 },
              '& .Mui-selected': { color: 'var(--color-accent) !important' },
              '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
            }}
          >
            {tabs.map((t) => <Tab key={t.value} label={t.label} value={t.value} />)}
          </Tabs>
          {active ? active.render() : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatisticsDialog;
